import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
import apiService from '../../../services/api';
import { useNotification } from '../../../contexts/NotificationContext';
import { usePageLoaderContext } from '../../../contexts/PageLoaderContext';
import SkeletonLoader from '../../UI/SkeletonLoader';
import styles from '../../../styles/PerformanceDashboard.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import dropdownStyles from '../../../styles/CustomDropdown.module.css';

// Components
import UserInfoCard from './components/UserInfoCard';
import MetricsGrid from './components/MetricsGrid';
import ChartsSection from './components/ChartsSection';
import RecentActivity from './components/RecentActivity';
import BreadcrumbNav from './components/BreadcrumbNav';
import FloatingNav from './components/FloatingNav';
import FloatingFilter from './components/FloatingFilter';
import TaskManagement from '../TaskManagement';
import CounsellingList from '../Counselling/CounsellingList';
import SidePanel from '../../UI/SidePanel';

import AppraisalReport from './AppraisalReport';
import CustomDropdown from '../../UI/CustomDropdown';

// Hooks
import { useCharts } from './hooks/useCharts';

// Types
import { UserPerformanceData } from './types';
import { CounsellingRecord } from '../Counselling/CounsellingCard';
import sidebarStyles from '../../../styles/SidePanel.module.css';

Chart.register(...registerables);

interface PerformanceDashboardProps {
  groupId?: string;
  userId?: string;
  excludeTaskManagement?: boolean;
  hideBreadcrumb?: boolean;
  customNavSections?: Array<{
    id: string;
    label: string;
    icon: React.ReactElement;
  }>;
}

const PerformanceDashboard: React.FC<PerformanceDashboardProps> = ({
  groupId: propGroupId,
  userId: propUserId,
  excludeTaskManagement = false,
  hideBreadcrumb = false,
  customNavSections,
}) => {
  const routeParams = useParams<{ groupId: string; userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const groupId = propGroupId || routeParams.groupId;
  const userId = propUserId || routeParams.userId;
  const notificationService = useNotification();

  // Determine if this is accessed from User Management
  const isFromUserManagement = location.pathname.startsWith('/user-management/');
  const { showLoader, hideLoader } = usePageLoaderContext();
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState<UserPerformanceData | null>(null);
  const [period, setPeriod] = useState<string>('all');
  const [groupDetails, setGroupDetails] = useState<{ id: string; name: string } | null>(null);
  const [badgesCount, setBadgesCount] = useState<number>(0);
  const [chartSelections, setChartSelections] = useState({
    taskTrend: 'completed-inprogress',
    taskRatio: 'completed-inprogress',
  });

  const [academicYearOptions, setAcademicYearOptions] = useState<{ value: string, label: string }[]>([]);
  const [counsellingRecords, setCounsellingRecords] = useState<CounsellingRecord[]>([]);
  const [isCounsellingLoading, setIsCounsellingLoading] = useState(false);
  const [selectedCounselling, setSelectedCounselling] = useState<CounsellingRecord | null>(null);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSection]);

  useEffect(() => {
    const fetchAcademicYears = async () => {
      try {
        const response = await apiService.getAcademicYears();
        // Assuming response is an array of objects
        if (Array.isArray(response)) {
          const options = response.map((year: any) => ({
            value: year.name,
            label: year.name
          }));
          setAcademicYearOptions(options);

          // Set default year if not set
          const currentYear = response.find((y: any) => y.is_current);
          if (currentYear) {
            setAppraisalYear(currentYear.name);
          } else if (options.length > 0) {
            setAppraisalYear(options[0].value);
          }
        }
      } catch (error) {
        console.error("Failed to fetch academic years:", error);
        // Fallback
        setAcademicYearOptions([
          { value: '2024-2025', label: '2024-2025' },
          { value: '2023-2024', label: '2023-2024' }
        ]);
      }
    };
    fetchAcademicYears();
  }, []);

  const [showAppraisalReport, setShowAppraisalReport] = useState(false);
  const [appraisalYear, setAppraisalYear] = useState('2024-2025');

  // Chart refs
  const statusChartRef = useRef<HTMLCanvasElement>(null);
  const typeChartRef = useRef<HTMLCanvasElement>(null);
  const pointsChartRef = useRef<HTMLCanvasElement>(null);
  const categoryChartRef = useRef<HTMLCanvasElement>(null);
  const taskTrendChartRef = useRef<HTMLCanvasElement>(null);
  const taskRatioChartRef = useRef<HTMLCanvasElement>(null);

  // Initialize charts
  useCharts(performanceData, {
    statusChartRef,
    typeChartRef,
    pointsChartRef,
    categoryChartRef,
    taskTrendChartRef,
    taskRatioChartRef,
  }, chartSelections);

  const handleChartSelectionChange = (chartId: string, selection: string) => {
    setChartSelections(prev => ({
      ...prev,
      [chartId]: selection,
    }));
  };

  const loadPerformanceData = async () => {
    if (!userId) return;

    setLoading(true);
    showLoader('Loading performance data...');
    try {
      let response;
      if (groupId) {
        // Use group-specific endpoint
        response = await apiService.getUserPerformance(groupId, userId, period);
      } else {
        // Use all-groups endpoint
        response = await apiService.getUserPerformanceAllGroups(userId, period);
      }

      if (response.status === 'success') {
        setPerformanceData(response.data);
        // Extract badges count from user info if available
        if (response.data?.user?.badges_count !== undefined) {
          setBadgesCount(response.data.user.badges_count);
        } else {
          // Fallback: fetch badges separately
          try {
            const badgesResponse = await apiService.getUserBadges(userId, groupId ? { group_id: groupId } : {});
            if (badgesResponse.status === 'success' && badgesResponse.data) {
              const badges = Array.isArray(badgesResponse.data) ? badgesResponse.data : (badgesResponse.data.badges || []);
              setBadgesCount(badges.length);
            }
          } catch (error) {
            console.error('Error loading badges:', error);
          }
        }
      } else {
        notificationService.error(response.message || 'Failed to load performance data');
      }
    } catch (error: any) {
      console.error('Error loading performance data:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load performance data. Please try again.');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const loadCounsellingData = async () => {
    if (!userId) return;
    setIsCounsellingLoading(true);
    console.log('Fetching counselling data for user:', userId);
    try {
      const response: any = await apiService.getCounsellingRecords({ created_by: userId });
      console.log('Counselling response received:', response);
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setCounsellingRecords(data);
    } catch (error) {
      console.error('Error loading counselling records:', error);
    } finally {
      setIsCounsellingLoading(false);
    }
  };

  const handleDownloadCounsellingReport = async (counselling: CounsellingRecord) => {
    showLoader('Generating report...');
    try {
      const blob = await apiService.downloadCounsellingReport(counselling.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Counselling_Report_${counselling.student_registration_number || counselling.student_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      notificationService.success('Report downloaded successfully');
    } catch (err: any) {
      console.error('Error downloading report:', err);
      notificationService.error('Failed to download report');
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (userId) {
      if (groupId) {
        loadGroupDetails();
      }
      loadPerformanceData();
    }
  }, [groupId, userId, period]);

  useEffect(() => {
    if (userId && activeSection === 'counselling-management') {
      loadCounsellingData();
    }
  }, [userId, activeSection]);

  const loadGroupDetails = async () => {
    if (!groupId) return;
    try {
      const response = await apiService.getGroupById(groupId);
      if (response.status === 'success') {
        setGroupDetails({
          id: response.data.id,
          name: response.data.name
        });
      }
    } catch (error: any) {
      console.error('Error loading group details:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load group details');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (!userId) {
    return (
      <div className={styles.errorContainer}>
        <p>User ID is required</p>
      </div>
    );
  }

  const defaultNavSections = [
    {
      id: 'home',
      label: 'Home',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
      ),
    },
    {
      id: 'performance-analytics',
      label: 'Performance Analytics',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="20" x2="18" y2="10"></line>
          <line x1="12" y1="20" x2="12" y2="4"></line>
          <line x1="6" y1="20" x2="6" y2="14"></line>
        </svg>
      ),
    },
    {
      id: 'recent-activity',
      label: 'Recent Activity',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
        </svg>
      ),
    },
    ...(!excludeTaskManagement ? [{
      id: 'task-management',
      label: 'Task Management',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      ),
    }] : []),
    {
      id: 'counselling-management',
      label: 'Counselling',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      ),
    },
  ];

  const navSections = customNavSections || defaultNavSections;

  const AppraisalControls = () => (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
      <CustomDropdown
        options={academicYearOptions}
        value={appraisalYear}
        onChange={(value: string | string[]) => setAppraisalYear(value as string)}
        placeholder="Select Year"
        size="sm"
        variant="outlined"
        className="filter"
      />

      <button
        onClick={() => setShowAppraisalReport(true)}
        className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
        style={{ borderRadius: '30px' }}
      >
        Generate Appraisal Report
      </button>
    </div>
  );

  return (
    <div className={styles.pageContainer}>
      {/* Page Header with Breadcrumb - Group context */}
      {!hideBreadcrumb && groupId && (
        <div className={styles.pageHeader} id="home">
          <div className={styles.headerContent} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <BreadcrumbNav
              groupId={groupId}
              groupName={groupDetails?.name}
              currentPage={performanceData ? performanceData.user.full_name : 'Performance Dashboard'}
            />
            {performanceData && <AppraisalControls />}
          </div>
        </div>
      )}

      {/* Page Header - User Management context (no group) */}
      {isFromUserManagement && !groupId && (
        <div className={styles.pageHeader} id="home">
          <div className={styles.headerContent} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <nav className={styles.breadcrumbNav}>
              <div className={styles.navPath}>
                <button
                  className={styles.navLink}
                  onClick={() => navigate('/user-management')}
                >
                  User Management
                </button>
                <span className={styles.navSeparator}>/</span>
                <span className={styles.navCurrent}>
                  {performanceData ? `${performanceData.user.full_name}'s Performance` : 'Performance Dashboard'}
                </span>
              </div>
            </nav>
            {performanceData && <AppraisalControls />}
          </div>
        </div>
      )}

      {/* Page Header - Standalone/Profile Context (Hidden Breadcrumb) */}
      {hideBreadcrumb && performanceData && (
        <div className={styles.pageHeader} id="home" style={{ marginBottom: '0rem' }}>
          <div className={styles.headerContent} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <AppraisalControls />
          </div>
        </div>
      )}

      <div className={styles.dashboardContainer} id={(hideBreadcrumb && !isFromUserManagement) ? "home" : undefined}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <SkeletonLoader variant="custom" count={5} height="200px" />
          </div>
        ) : performanceData ? (

          <>
            {activeSection === 'home' && (
              <>
                <UserInfoCard
                  user={performanceData.user}
                  formatDate={formatDate}
                  statistics={performanceData.statistics}
                  badgesCount={badgesCount}
                />
                <div id="performance-overview">
                  <MetricsGrid performanceData={performanceData} />
                </div>
              </>
            )}

            {activeSection === 'performance-analytics' && (
              <div id="performance-analytics">
                <ChartsSection
                  taskTrendChartRef={taskTrendChartRef}
                  statusChartRef={statusChartRef}
                  taskRatioChartRef={taskRatioChartRef}
                  pointsChartRef={pointsChartRef}
                  performanceData={performanceData}
                  onChartSelectionChange={handleChartSelectionChange}
                  chartSelections={chartSelections}
                />
              </div>
            )}

            {activeSection === 'recent-activity' && (
              <div id="recent-activity">
                <RecentActivity
                  performanceData={performanceData}
                  formatDate={formatDate}
                  formatTime={formatTime}
                />
              </div>
            )}

            {/* Task Management Section */}
            {activeSection === 'task-management' && !excludeTaskManagement && (
              <div id="task-management" className={styles.activitySection}>
                <div className={styles.sectionHeader}>
                  <h2 className={styles.sectionTitle}>Task Management</h2>
                  <p className={styles.sectionSubtitle}>Manage your assigned tasks, self-assigned tasks, and approvals</p>
                </div>
                <TaskManagement embedded={true} userId={userId} />
              </div>
            )}

            {/* Counselling Management Section */}
            {activeSection === 'counselling-management' && (
              <div id="counselling-management" className={styles.activitySection}>
                <div className={styles.sectionHeader}>
                  <h2 className={styles.sectionTitle}>Counselling Management</h2>
                  <p className={styles.sectionSubtitle}>View and manage counselling records created by this faculty</p>
                </div>
                <div>
                  <CounsellingList
                    counsellings={counsellingRecords}
                    loading={isCounsellingLoading}
                    error={null}
                    isInitialLoad={false}
                    onCounsellingClick={(c) => setSelectedCounselling(c)}
                    onRetry={loadCounsellingData}
                    onDownload={handleDownloadCounsellingReport}
                    showEditDelete={false}
                    emptyStateTitle="No Counselling Records"
                    emptyStateDescription="This faculty hasn't created any counselling records yet."
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={styles.errorContainer}>
            <p>Failed to load performance data</p>
          </div>
        )}


        {/* Appraisal Report Modal */}
        {showAppraisalReport && userId && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ background: '#fff', width: '90%', height: '90%', overflowY: 'auto', borderRadius: '8px', position: 'relative', padding: 0 }}>
              <AppraisalReport
                userId={userId}
                academicYear={appraisalYear}
                onClose={() => setShowAppraisalReport(false)}
              />
            </div>
          </div>
        )}

        {/* Counselling Details Side Panel */}
        <SidePanel
          isOpen={!!selectedCounselling}
          onClose={() => setSelectedCounselling(null)}
          title="Counselling Details"
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
            </svg>
          }
        >
          {selectedCounselling && (
            <div className={sidebarStyles.detailsContent}>
              {/* Action Bar */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '0.5rem 0',
                marginBottom: '1rem',
                borderBottom: '1px solid var(--border-color)',
                gap: '0.75rem'
              }}>
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                  onClick={() => handleDownloadCounsellingReport(selectedCounselling)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.875rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    borderColor: '#D1FAE5',
                    backgroundColor: '#ECFDF5',
                    color: '#059669'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                  </svg>
                  Download Report
                </button>
              </div>

              <div className={sidebarStyles.detailsSection}>
                <h3 className={sidebarStyles.sectionTitleSmall}>Student Info</h3>
                <div className={sidebarStyles.detailsGrid}>
                  <div className={sidebarStyles.detailItem}>
                    <label>Name</label>
                    <p>{selectedCounselling.student_name}</p>
                  </div>
                  <div className={sidebarStyles.detailItem}>
                    <label>Reg No</label>
                    <p>{selectedCounselling.student_registration_number || 'N/A'}</p>
                  </div>
                  <div className={sidebarStyles.detailItem}>
                    <label>Year / Sem</label>
                    <p>{selectedCounselling.year} / {selectedCounselling.semester}</p>
                  </div>
                  <div className={sidebarStyles.detailItem}>
                    <label>Created On</label>
                    <p>{new Date(selectedCounselling.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              <div className={sidebarStyles.detailsSection}>
                <h3 className={sidebarStyles.sectionTitleSmall}>Academic Profile</h3>
                <div className={sidebarStyles.detailsGrid}>
                  <div className={sidebarStyles.detailItem}>
                    <label>Difficult Subjects</label>
                    <p>{selectedCounselling.difficult_subjects || 'None reported'}</p>
                    {selectedCounselling.difficult_subjects_reason && (
                      <p className={sidebarStyles.detailReason}>{selectedCounselling.difficult_subjects_reason}</p>
                    )}
                  </div>
                  <div className={sidebarStyles.detailItem}>
                    <label>Easy Subjects</label>
                    <p>{selectedCounselling.easy_subjects || 'None reported'}</p>
                    {selectedCounselling.easy_subjects_reason && (
                      <p className={sidebarStyles.detailReason}>{selectedCounselling.easy_subjects_reason}</p>
                    )}
                  </div>
                  <div className={sidebarStyles.detailItem}>
                    <label>Textbooks</label>
                    <p>{selectedCounselling.have_textbooks || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div className={sidebarStyles.detailsSection}>
                <h3 className={sidebarStyles.sectionTitleSmall}>Lifestyle & Routine</h3>
                <div className={sidebarStyles.detailsGrid}>
                  <div className={sidebarStyles.detailItem}>
                    <label>Study Hours</label>
                    <p>{selectedCounselling.study_hours || 'N/A'} hrs/day</p>
                  </div>
                  <div className={sidebarStyles.detailItem}>
                    <label>Record Work</label>
                    <p>{selectedCounselling.record_hours || 'N/A'} hrs/day</p>
                  </div>
                  <div className={sidebarStyles.detailItem}>
                    <label>Entertainment</label>
                    <p>{selectedCounselling.entertainment_hours || 'N/A'} hrs/day</p>
                  </div>
                  <div className={sidebarStyles.detailItem}>
                    <label>Transport</label>
                    <p>{selectedCounselling.transport_mode || 'N/A'} ({selectedCounselling.journey_time || 'N/A'})</p>
                  </div>
                </div>
              </div>

              <div className={sidebarStyles.detailsSection}>
                <h3 className={sidebarStyles.sectionTitleSmall}>Peer & Family Interaction</h3>
                <div className={sidebarStyles.detailsGrid}>
                  <div className={sidebarStyles.detailItem}>
                    <label>Best Friends</label>
                    <p>{selectedCounselling.best_friends || 'N/A'}</p>
                  </div>
                  <div className={sidebarStyles.detailItem}>
                    <label>Parent Interaction</label>
                    <p>{selectedCounselling.parent_interaction || 'N/A'}</p>
                  </div>
                  <div className={sidebarStyles.detailItem}>
                    <label>Faculty Encouragement</label>
                    <p>{selectedCounselling.faculty_encouragement || 'N/A'}</p>
                  </div>
                  <div className={sidebarStyles.detailItem}>
                    <label>Disturbances</label>
                    <p>{selectedCounselling.disturbances || 'No'}</p>
                  </div>
                </div>
              </div>

              {selectedCounselling.attendance_months && selectedCounselling.attendance_months.length > 0 && (
                <div className={sidebarStyles.detailsSection}>
                  <h3 className={sidebarStyles.sectionTitleSmall}>Monthly Attendance (%)</h3>
                  <table className={sidebarStyles.detailsTable}>
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCounselling.attendance_months.map((m, i) => (
                        <tr key={i}>
                          <td>{m.month}</td>
                          <td>{m.percent}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedCounselling.counsellor_comments && selectedCounselling.counsellor_comments.length > 0 && (
                <div className={sidebarStyles.detailsSection}>
                  <h3 className={sidebarStyles.sectionTitleSmall}>Counsellor Interaction Logs</h3>
                  <div className={sidebarStyles.commentsList}>
                    {selectedCounselling.counsellor_comments.map((c, i) => (
                      <div key={i} className={sidebarStyles.commentItem}>
                        <div className={sidebarStyles.commentHeader}>
                          <span className={sidebarStyles.commentDate}>{c.date}</span>
                        </div>
                        <p className={sidebarStyles.commentText}>{c.comment}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className={sidebarStyles.detailsSection}>
                <h3 className={sidebarStyles.sectionTitleSmall}>Key Suggestions</h3>
                <div className={sidebarStyles.detailsGrid}>
                  <div className={sidebarStyles.detailItem}>
                    <label>Remedial Measures</label>
                    <p>{selectedCounselling.remedial_suggestions || 'None'}</p>
                  </div>
                  <div className={sidebarStyles.detailItem}>
                    <label>Counselling Advice</label>
                    <p>{selectedCounselling.counselling_suggestions || 'None'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </SidePanel>
      </div>

      {/* Floating Navigation */}
      {performanceData && (
        <FloatingNav
          sections={navSections}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
      )}

      {/* Floating Date Filter (UI only; hook into API later if needed) */}
      {performanceData && <FloatingFilter />}
    </div >
  );
};

export default PerformanceDashboard;
